import { Link } from "wouter";
import { ArrowRight } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import AnimatedSection from "@/components/AnimatedSection";
import PageHero from "@/components/PageHero";
import { IMAGES } from "@/lib/constants";
import LazyImage from "@/components/LazyImage";
import RelatedArticles from "@/components/RelatedArticles";

const services = [
  {
    title: "Laserskärning",
    desc: "Tre moderna CO2-lasrar från Trumpf och Amada med upp till 6 kW effekt. Vi skär i stål, rostfritt och aluminium med tjocklekar från 0,5 mm till 25 mm. Toleranser ner till 0,1 mm garanterat.",
    href: "/tjanster/laserskarning",
    image: IMAGES.heroLaser,
    specs: ["Upp till 6 kW effekt", "Max 25 mm stål", "0,1 mm tolerans", "Enkla detaljer ofta samma dag"],
  },
  {
    title: "Kantpressning",
    desc: "Tre kantpressar med upp till 310 tons kapacitet från Ursviken och Amada. Vi bockar plåt med hög precision, från prototyper till stora serier. Kombinera med laserskärning för en komplett lösning.",
    href: "/tjanster/kantpressning",
    image: IMAGES.workshop3,
    specs: ["Upp till 310 ton", "3 kantpressar", "Hög precision", "Prototyp till serie"],
  },
  {
    title: "Svetsning",
    desc: "Kvalificerad svetsning som komplement till skärning och bockning. Vi levererar färdiga komponenter, skurna, bockade, svetsade och ytbehandlade. En leverantör för hela kedjan.",
    href: "/tjanster/svetsning",
    image: IMAGES.workshop2,
    specs: ["Flera svetsmetoder", "Flera material", "Komplett lösning", "Ytbehandling"],
  },
];

export default function Tjanster() {
  return (
    <Layout>
      <SEOHead
        title="Plåtbearbetning och legotillverkning i Eskilstuna | ASPAB"
        description="Plåtbearbetning och legotillverkning i Eskilstuna: laserskärning, kantpressning, svetsning och ytbehandling hos en leverantör. Begär offert."
        canonical="https://aspab.com/tjanster"
        path="/tjanster"
        breadcrumbs={[{ name: "Tjänster", href: "/tjanster" }]}
              ogImage={IMAGES.ogTjanster}
      />

      <PageHero
        title="Plåtbearbetning och legotillverkning i Eskilstuna"
        subtitle="Från ritning till färdig komponent med laserskärning, kantpressning, svetsning och ytbehandling hos en leverantör."
        breadcrumbs={[{ name: "Tjänster", href: "/tjanster" }]}
      />

      <section className="bg-white py-14 md:py-18 border-b border-gray-200">
        <div className="container">
          <AnimatedSection>
            <div className="max-w-4xl">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-gray-900">
                En komplett underleverantör för plåt och metall
              </h2>
              <p className="text-gray-600 mt-4 text-lg leading-relaxed">
                ASPAB hjälper industriföretag med legotillverkning och plåtbearbetning i Eskilstuna. Vi samlar CO2-laserskärning, kantpressning, svetsning, stansning, fräsning och ytbehandling i en sammanhållen produktionskedja. Det ger färre överlämningar, bättre kontroll och en tydlig kontakt från offert till leverans.
              </p>
              <p className="text-gray-600 mt-4 leading-relaxed">
                Vi arbetar med enstaka prototyper, återkommande artiklar och större serier i stål, rostfritt, aluminium och höghållfasthetsstål. Skicka ritning och uppgifter om material, tjocklek och antal så återkommer vi med ett tillverkningsförslag och en offert.
              </p>
              <div className="mt-6 flex flex-wrap gap-4">
                <Link href="/kontakt" className="inline-flex items-center gap-2 px-6 py-3 bg-[#2563EB] text-white font-semibold rounded-lg hover:bg-[#1D4ED8] transition-colors">
                  Begär offert <ArrowRight className="w-4 h-4" />
                </Link>
                <Link href="/maskinpark" className="inline-flex items-center gap-2 px-6 py-3 border border-gray-300 text-gray-800 font-semibold rounded-lg hover:border-[#2563EB] hover:text-[#2563EB] transition-colors">
                  Se vår maskinpark
                </Link>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28">
        <div className="container space-y-16">
          {services.map((svc, i) => (
            <AnimatedSection key={svc.title} delay={i * 100}>
              <div className={`grid lg:grid-cols-2 gap-8 items-center ${i % 2 === 1 ? "lg:direction-rtl" : ""}`}>
                <div className={`overflow-hidden rounded-lg ${i % 2 === 1 ? "lg:order-last" : ""}`}>
                  <LazyImage
                    src={svc.image}
                    alt={`${svc.title} hos ASPAB`}
                    className="w-full aspect-[16/10] object-cover"
                    wrapperClassName="w-full"
                  />
                </div>
                <div className={i % 2 === 1 ? "lg:order-first" : ""}>
                  <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900">{svc.title}</h2>
                  <p className="text-gray-500 mt-4 leading-relaxed">{svc.desc}</p>

                  <div className="grid grid-cols-2 gap-3 mt-6">
                    {svc.specs.map((spec) => (
                      <div key={spec} className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#2563EB]" />
                        <span className="text-gray-500 text-sm">{spec}</span>
                      </div>
                    ))}
                  </div>

                  <Link
                    href={svc.href}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-[#2563EB] text-white font-semibold rounded-lg hover:bg-[#3B82F6] transition-colors mt-6"
                  >
                    Läs mer om {svc.title.toLowerCase()} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* Additional services */}
      <section className="bg-gray-50 py-20 md:py-28 border-t border-gray-200">
        <div className="container">
          <AnimatedSection>
            <div className="text-center mb-12">
              <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">Kompletterande tjänster</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                Mer än bara skärning
              </h2>
            </div>
          </AnimatedSection>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {[
              { title: "Rörlaser", desc: "Skärning av profiler, fyrkantsprofiler och rör med vår Trumpf rörlaser." },
              { title: "Stansning", desc: "Finn Power stansmaskin för hål och former i plåt." },
              { title: "Fräsning", desc: "Hedelius borr- och fräsmaskin för krävande bearbetning." },
              { title: "Pulverlackering", desc: "Egen kapacitet för ytbehandling av färdiga komponenter." },
            ].map((svc, i) => (
              <AnimatedSection key={svc.title} delay={i * 100}>
                <div className="bg-white border border-gray-200 rounded-lg p-6 h-full hover:border-[#2563EB]/20 transition-colors">
                  <h3 className="font-heading text-lg font-semibold text-gray-900">{svc.title}</h3>
                  <p className="text-gray-500 text-sm mt-2 leading-relaxed">{svc.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Kunskapsbank */}
      <RelatedArticles
        heading="Fördjupa dig i våra tjänster"
        subheading="Läs våra expertartiklar om laserskärning, kantpressning, svetsning och materialval."
        articles={[
          { title: "Laserskärning med hög precision", slug: "laserskarning", href: "/tjanster/laserskarning", typeLabel: "Tjänst", ctaLabel: "Läs mer", excerpt: "Läs om ASPAB:s kapacitet, material, toleranser och leveranser inom industriell laserskärning." },
          { title: "Kantpressning: den kompletta guiden", slug: "kantpressning-guide", excerpt: "Allt du behöver veta om kantpressning – från grunderna till avancerade tekniker." },
          { title: "Svetsmetoder för industriell plåt", slug: "svetsning-metoder-industri", excerpt: "MIG, TIG och MAG – vilken svetsmetod passar bäst för ditt projekt?" },
          { title: "Laserskärning vs plasmaskärning", slug: "laserskarning-vs-plasmaskaring", excerpt: "Vilken skärmetod passar bäst? Vi jämför precision, kostnad och materialkapacitet." },
          { title: "Materialguide: stål, rostfritt, aluminium", slug: "materialguide-stal-rostfritt-aluminium", excerpt: "Egenskaper och val av rätt material för ditt projekt." },
          { title: "Så förbereder du CAD-filer", slug: "sa-forbereder-du-cad-filer-for-laserskarning", excerpt: "Rätt filformat, toleranser och vanliga misstag att undvika." },
        ]}
      />
    </Layout>
  );
}
